import React from 'react'

import { Heading, ScrollList } from '@hhgtech/hhg-components'
import { Container as MantineContainer, rem, Stack } from '@mantine/core'
import innertext from 'innertext'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import { Container } from 'components/atoms/container'
import { AdBlockContainer } from 'components/molecules/adBlock/adBlockContainer'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/deferFixedBottomAdBlock'
import { CollapsibleFaqs } from 'components/molecules/collapsibleFaqs'
import { SpotlightsExpert } from 'components/molecules/spotlightExpert'
import { SpotlightSubotList } from 'components/molecules/subotEmbedded'
import { TwoColumnsContainer } from 'components/molecules/twoColumnsContainer'
import { EmbeddedCommunity } from 'components/organisms/embededCommunity'
import { BabyPoopCard } from 'components/organisms/embededHeathToolCards/baby-poop'
import { BabyVaccinationCard } from 'components/organisms/embededHeathToolCards/baby-vaccination'
import { BabyGrowthCard } from 'components/organisms/embededHeathToolCards/embedBabyGrowth'
import { BMICardWrapper } from 'components/organisms/embededHeathToolCards/embedBMI'
import { BMRCardWrapper } from 'components/organisms/embededHeathToolCards/embedBMR'
import { DueDateCardWrapper } from 'components/organisms/embededHeathToolCards/embedDueDate'
import { OvulationCardWrapper } from 'components/organisms/embededHeathToolCards/embedOvulation'
import { PWGCardWrapper } from 'components/organisms/embededHeathToolCards/embedPwg'
import { THRCardWrapper } from 'components/organisms/embededHeathToolCards/embedTHR'
import { HealthToolsCard } from 'components/organisms/homepageHealthToolsCard'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { VideoList } from 'components/organisms/videoLists'
import { MastheadAd } from 'components/pages/article/components/MastheadAd'
import { OutOfPageAd } from 'components/pages/article/components/OutOfPageAd'
import { LandingPageBanner } from 'components/pages/landingPage/banner'
import { GeneralSection } from 'components/pages/landingPage/generalSection'
import {
  StyledGeneralSectionContainer,
  StyledLandingWrapper,
  StyledToolsCard,
} from 'components/pages/landingPage/index.styled'
import { InformationSection } from 'components/pages/landingPage/infomationSection'
import { SpotlightList } from 'components/pages/landingPage/spotlightList'
// import { VideosSection } from 'components/pages/landingPage/videos'
import { useMastheadAdEnable } from 'hooks/useMastheadAdEnable'
import { getSpotlightLocaleAdMappingFromSlug } from 'interfaces/adCategoryMapping'
import {
  HealthTool,
  Spotlight,
  SpotlightExpert,
  SpotlightPageBanner,
  SpotlightPost,
} from 'interfaces/types'
import { getAdTag, getCountryAdTag } from 'utils/ads'

const AdBlock = dynamic(
  () => import('components/molecules/adBlock').then((m) => m.AdBlock),
  {
    ssr: false,
  },
)

export type Props = {
  isMobile: boolean
  spotlight: Spotlight
  healthTools: HealthTool[]
}

const SpotlightDefaultTemplate = ({
  isMobile,
  spotlight,
  healthTools,
}: Props) => {
  const { locale = 'en-US' } = useRouter()

  const finalSlug = spotlight.permalink?.split('/').filter(Boolean).pop()

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: {
      result:
        getSpotlightLocaleAdMappingFromSlug(locale, finalSlug || '') ||
        'Spotlight',
      redundant: true,
    },
  })
  const mastheadEnable = useMastheadAdEnable(spotlight?.adsOnArticles || {})

  const isVideosAtMiddle = spotlight.posts.some(
    (post: SpotlightPost) => post.enableVideo === true,
  )

  const isHavingSubot = spotlight?.subot && spotlight?.subot?.post?.length > 0
  const isHavingExpertsList =
    spotlight?.expert && spotlight?.expert?.experts?.length > 0
  const isHavingFaqs = spotlight?.faqs && spotlight?.faqs.length > 0

  const slideToShow = isMobile ? 1.2 : 2.5
  const customTargetings = {
    Is_Sponsored: [1],
    Is_Expert: [spotlight?.expert?.experts?.[0]?.id ? 1 : 0],
    Is_Affiliate: [0],
    Brand: spotlight?.sponsor?.map((s) => innertext(s.title || '')) || [],
    // [spotlight?.hasAffiliate ? 1 : 0],
  }

  return (
    <StyledLandingWrapper>
      {/* https://hhgdev.atlassian.net/browse/HHD-6441 */}
      <AdBlockContainer>
        <MastheadAd
          adUnitPath={adUnitPath}
          className={`spotlight-supercontent-masthead ${spotlight?.type}-masthead`}
          hasPlaceholder={mastheadEnable}
          containerClassName="z-[11]"
        />
      </AdBlockContainer>
      <LandingPageBanner
        bannerUrl={'https://picsum.photos/1440/300'}
        banner={spotlight.banner as SpotlightPageBanner}
      />
      <SpotlightList
        spotLights={spotlight.posts.map((post) => ({
          name: post.title,
        }))}
        isMobile={isMobile}
        isSponsored={spotlight?.isSponsor}
        applyH1
      />
      {/* {spotlight?.clickable_banner && (
        <ClickableBanner banner={spotlight.clickable_banner} />
      )} */}
      {(isHavingSubot || isHavingExpertsList || isHavingFaqs) && (
        <Container
          style={{
            maxWidth: '1170px',
            marginTop: isMobile ? 24 : 64,
            padding: '0 16px',
          }}
        >
          {isHavingSubot && (
            <SpotlightSubotList
              isMobile={isMobile}
              spotlightSubot={spotlight?.subot}
            />
          )}
          {isHavingExpertsList && (
            <>
              <Heading tag="h3" style={{ margin: '60px 0 16px 0' }}>
                {spotlight?.expert?.title}
              </Heading>
              {spotlight?.expert?.experts?.length > 0 && (
                <ScrollList
                  row={1}
                  column={3}
                  isDotsShown={!isMobile}
                  isArrowShown={!isMobile}
                  mode="snapped"
                >
                  {spotlight?.expert?.experts.map(
                    (expert: SpotlightExpert, index: number) => (
                      <SpotlightsExpert
                        key={index}
                        isMobile={isMobile}
                        expertInfo={expert}
                        isSingleExpert={
                          spotlight?.expert?.experts?.length === 1
                        }
                      />
                    ),
                  )}
                </ScrollList>
              )}
            </>
          )}
          {isHavingFaqs && (
            <CollapsibleFaqs
              isMobile={isMobile}
              posts={spotlight?.faqs}
              title={spotlight?.faqsTitle}
            />
          )}
        </Container>
      )}
      <StyledGeneralSectionContainer>
        {spotlight.posts.length > 0 && (
          <>
            <GeneralSection
              title={spotlight.posts[0].title}
              posts={spotlight.posts[0].post}
            />
            {spotlight.posts[0].enableVideo && spotlight.videos?.videos && (
              <MantineContainer size="lg">
                <VideoList
                  videos={spotlight.videos.videos.map((url) => ({
                    videoUrl: url.video,
                  }))}
                  slideToShow={slideToShow}
                />
              </MantineContainer>
            )}
          </>
        )}
      </StyledGeneralSectionContainer>

      {isMobile ? (
        <>
          {!spotlight?.disableAd && (
            <AdBlock
              adUnitPath={adUnitPath}
              size={[
                [300, 250],
                [300, 600],
              ]}
              divId={'div-gpt-ad-home-sidebar3'}
              pos={['MidBanner1']}
              key={isMobile ? 'mobile' : 'desktop'}
              customTargetings={customTargetings}
            />
          )}
          {spotlight.posts
            .slice(1, spotlight.posts.length)
            .map((item, index) => (
              <div key={index}>
                {item.typeView === 'advance' ? (
                  <>
                    <GeneralSection title={item.title} posts={item.post} />
                    {item.enableVideo && spotlight.videos?.videos && (
                      <MantineContainer size="lg">
                        <VideoList
                          videos={spotlight.videos.videos.map((url) => ({
                            videoUrl: url.video,
                          }))}
                          slideToShow={slideToShow}
                        />
                      </MantineContainer>
                    )}
                  </>
                ) : (
                  <>
                    <InformationSection
                      posts={item.post}
                      title={item.title}
                      viewType={item.typeView}
                      isMobile={isMobile}
                      columns={2}
                    />
                    {item.enableVideo && spotlight.videos?.videos && (
                      <MantineContainer size="lg">
                        <VideoList
                          videos={spotlight.videos.videos.map((url) => ({
                            videoUrl: url.video,
                          }))}
                          slideToShow={slideToShow}
                        />
                      </MantineContainer>
                    )}
                  </>
                )}
              </div>
            ))}
          {!spotlight.disableAd && (
            <AdBlock
              adUnitPath={adUnitPath}
              size={[
                [300, 250],
                [300, 600],
              ]}
              divId={'div-gpt-ad-home-sidebar4'}
              pos={['MidBanner2']}
              key={isMobile ? 'mobile2' : 'desktop2'}
              customTargetings={customTargetings}
            />
          )}
          <Container>
            {spotlight.healthTool === 'bmi' && (
              <BMICardWrapper
                articleCategories={null}
                positionType="spotlight"
              />
            )}
            {spotlight.healthTool === 'bmr' && (
              <BMRCardWrapper
                articleCategories={null}
                positionType="spotlight"
              />
            )}
            {spotlight.healthTool === 'pregnancy-weight-gain' && (
              <PWGCardWrapper />
            )}
            {spotlight.healthTool === 'heart-rate' && <THRCardWrapper />}
            {spotlight.healthTool === 'ovulation' && <OvulationCardWrapper />}
            {spotlight.healthTool === 'due-date' && <DueDateCardWrapper />}
            {spotlight.healthTool === 'vaccination-tool' && (
              <BabyVaccinationCard />
            )}
            {spotlight.healthTool === 'baby-poop-tool' && (
              <BabyPoopCard direction="column" />
            )}
            {spotlight.healthTool === 'child-growth-chart' && (
              <BabyGrowthCard />
            )}
            {spotlight?.community && (
              <EmbeddedCommunity
                columns={1}
                isMobile={isMobile}
                communityId={spotlight?.community?.id}
                titleMember={spotlight?.community?.memberTitle}
                titleGuest={spotlight?.community?.guestTitle}
                descriptionMember={spotlight?.community?.memberDesc}
                descriptionGuest={spotlight?.community?.guestDesc}
              />
            )}
          </Container>
        </>
      ) : (
        <Container>
          <TwoColumnsContainer
            leftColumn={
              <>
                {spotlight.posts.slice(1, 3).map((item, index) => (
                  <React.Fragment key={index}>
                    <InformationSection
                      posts={item.post}
                      title={item.title}
                      viewType={item.typeView}
                      isMobile={isMobile}
                      columns={2}
                    />
                    {item.enableVideo && spotlight.videos?.videos && (
                      <MantineContainer size="lg">
                        <VideoList
                          videos={spotlight.videos.videos.map((url) => ({
                            videoUrl: url.video,
                          }))}
                          slideToShow={slideToShow}
                        />
                      </MantineContainer>
                    )}
                  </React.Fragment>
                ))}
              </>
            }
            rightColumn={
              <StyledToolsCard>
                <Stack spacing={rem(24)}>
                  {!spotlight.disableAd && (
                    <AdBlock
                      adUnitPath={adUnitPath}
                      size={[
                        [300, 250],
                        [300, 600],
                      ]}
                      divId={'div-gpt-ad-home-sidebar1'}
                      pos={['MidBanner1']}
                      key={isMobile ? 'mobile' : 'desktop'}
                      style={{
                        marginBlock: '1rem',
                        maxWidth: '100%',
                      }}
                      customTargetings={customTargetings}
                    />
                  )}

                  {spotlight.healthTool === 'bmi' && (
                    <BMICardWrapper
                      articleCategories={null}
                      positionType="spotlight"
                      isSide
                    />
                  )}
                  {spotlight.healthTool === 'bmr' && (
                    <BMRCardWrapper
                      articleCategories={null}
                      positionType="spotlight"
                      isSide
                    />
                  )}
                  {spotlight.healthTool === 'pregnancy-weight-gain' && (
                    <PWGCardWrapper isSide />
                  )}
                  {spotlight.healthTool === 'heart-rate' && (
                    <THRCardWrapper isSide />
                  )}
                  {spotlight.healthTool === 'ovulation' && (
                    <OvulationCardWrapper isSide />
                  )}
                  {spotlight.healthTool === 'due-date' && (
                    <DueDateCardWrapper isSide />
                  )}
                  {spotlight.healthTool === 'vaccination-tool' && (
                    <BabyVaccinationCard isSide />
                  )}
                  {spotlight.healthTool === 'child-growth-chart' && (
                    <BabyGrowthCard isSide />
                  )}
                  {spotlight.healthTool === 'baby-poop-tool' && (
                    <BabyPoopCard direction="column" isSide />
                  )}

                  {!!spotlight.healthTool && (
                    <HealthToolsCard
                      healthToolsLoading={false}
                      healthTools={healthTools}
                      isMobile={isMobile}
                    />
                  )}
                  {spotlight?.community && (
                    <EmbeddedCommunity
                      columns={1}
                      isMobile={isMobile}
                      communityId={spotlight?.community?.id}
                      titleMember={spotlight?.community?.memberTitle}
                      titleGuest={spotlight?.community?.guestTitle}
                      descriptionMember={spotlight?.community?.memberDesc}
                      descriptionGuest={spotlight?.community?.guestDesc}
                    />
                  )}
                  {!spotlight.disableAd && (
                    <AdBlock
                      adUnitPath={adUnitPath}
                      size={[
                        [300, 250],
                        [300, 600],
                      ]}
                      divId={'div-gpt-ad-home-sidebar2'}
                      pos={['MidBanner2']}
                      key={isMobile ? 'mobile2' : 'desktop2'}
                      style={{
                        marginBlock: '1rem',
                        maxWidth: '100%',
                      }}
                      customTargetings={customTargetings}
                    />
                  )}
                </Stack>
              </StyledToolsCard>
            }
          />
          {spotlight.posts
            .slice(3, spotlight.posts.length)
            .map((item, index) => (
              <div key={index}>
                {item.typeView === 'advance' ? (
                  <>
                    <GeneralSection title={item.title} posts={item.post} />
                    {item.enableVideo && spotlight.videos?.videos && (
                      <MantineContainer size="lg">
                        <VideoList
                          videos={spotlight.videos.videos.map((url) => ({
                            videoUrl: url.video,
                          }))}
                          slideToShow={slideToShow}
                        />
                      </MantineContainer>
                    )}
                  </>
                ) : (
                  <>
                    <InformationSection
                      posts={item.post}
                      title={item.title}
                      viewType={item.typeView}
                      isMobile={isMobile}
                      columns={4}
                    />
                    {item.enableVideo && spotlight.videos?.videos && (
                      <MantineContainer size="lg">
                        <VideoList
                          videos={spotlight.videos.videos.map((url) => ({
                            videoUrl: url.video,
                          }))}
                          slideToShow={slideToShow}
                        />
                      </MantineContainer>
                    )}
                  </>
                )}
              </div>
            ))}
        </Container>
      )}
      {spotlight.videos?.videos && !isVideosAtMiddle && (
        <MantineContainer size="lg">
          <VideoList
            videos={spotlight.videos.videos.map((url) => ({
              videoUrl: url.video,
            }))}
            slideToShow={slideToShow}
          />
        </MantineContainer>
        // <VideosSection isMobile={isMobile} videoData={spotlight.videos} />
      )}

      <MobileBottomNavigationWrapper />
      {!spotlight.disableAd && (
        <AdBlockContainer>
          <FixedBottomAdBlock
            divId={'div-gpt-ad-category-sticky'}
            className="article-bottom-ad"
            adUnitPath={adUnitPath}
            isMobile={isMobile}
            key={isMobile ? 'mobile' : 'desktop'}
            customTargetings={customTargetings}
          />
          <OutOfPageAd id={`div-gpt-out-of-page`} adUnitPath={adUnitPath} />
        </AdBlockContainer>
      )}
    </StyledLandingWrapper>
  )
}

export default SpotlightDefaultTemplate
